import { Injectable } from '@angular/core';
import { EventBusService } from './event-bus.service';
import { AppEvent } from '@models/events/app-event.enum';
import { Reservation } from '@models/reservation.model';
import { PurchaseContainer } from '@models/purchase-container.model';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { ConfigService } from './config.service';

// In the /assets/js/gtag.js file
declare function initGoogleAnalytics(googleAnalyticsId: string): any;
declare const gtag: Function;
declare const gtagEcommerce: Function;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  allowed: boolean = false;
  gtmId: string | undefined;
  private utmParams: any = {};

  constructor(
    private eventBusService: EventBusService,
    private cookieService: NgcCookieConsentService,
    private configService: ConfigService
  ) {}

  init() {
    this.gtmId = this.configService.settings?.gtm_id;

    if (this.gtmId && this.cookieService.hasConsented()) {
      this.captureUTMParams();
      this.includeGTM();
      this.allowed = true;
      this.subscribeToEvents();

      // Send UTM params to GA
      if (Object.keys(this.utmParams).length > 0) {
        this.trackUTM();
      }
    }
  }

  formatReservationItems(reservation: Reservation) {
    const items: any[] = [];

    reservation.items.forEach((item: any) => {
      items.push({
        item_id: item.id,
        item_name: item.ticket_type.name,
        price: item.ticket_type.price,
        quantity: item.quantity,
      });
    });

    return items;
  }

  private subscribeToEvents(): void {
    if (this.gtmId) {
      this.eventBusService.on(
        AppEvent.AddToCart,
        (reservation: Reservation) => {
          gtagEcommerce({
            event: 'add_to_cart',
            ecommerce: {
              currency: reservation.event?.currency ?? 'AED',
              value: reservation.total_price,
              items: this.formatReservationItems(reservation),
            },
          });
        }
      );

      this.eventBusService.on(
        AppEvent.InitiateCheckout,
        (reservation: Reservation) => {
          gtagEcommerce({
            event: 'begin_checkout',
            ecommerce: {
              currency: reservation.event?.currency ?? 'AED',
              value: reservation.total_price,
              items: this.formatReservationItems(reservation),
            },
          });
        }
      );

      this.eventBusService.on(
        AppEvent.Purchase,
        (purchaseContainer: PurchaseContainer) => {
          gtagEcommerce({
            event: 'purchase',
            ecommerce: {
              transaction_id: purchaseContainer.order.id,
              currency: purchaseContainer.order.currency,
              value: purchaseContainer.order.amount,
              items: this.formatReservationItems(purchaseContainer.reservation),
            },
          });
        }
      );
    }
  }

  private captureUTMParams(): void {
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmTerm = urlParams.get('utm_term');
    const utmContent = urlParams.get('utm_content');

    this.utmParams = {};
    if (utmSource) this.utmParams.campaign_source = utmSource;
    if (utmMedium) this.utmParams.campaign_medium = utmMedium;
    if (utmCampaign) this.utmParams.campaign_name = utmCampaign;
    if (utmTerm) this.utmParams.campaign_term = utmTerm;
    if (utmContent) this.utmParams.campaign_content = utmContent;

    // Store UTM in session storage
    if (Object.keys(this.utmParams).length > 0) {
      sessionStorage.setItem('utm_params', JSON.stringify(this.utmParams));
    } else {
      const stored = sessionStorage.getItem('utm_params');
      if (stored) this.utmParams = JSON.parse(stored);
    }
  }

  private trackUTM(): void {
    gtag('event', 'utm_attribution', {
      ...this.utmParams,
    });
  }

  includeGTM() {
    if (!this.gtmId) return;

    // Ensure dataLayer exists before GTM script loads
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // Load GTM external script
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;
    document.head.appendChild(gtmScript);

    // Add GTM noscript iframe for non-JS browsers
    const noScript = document.createElement('noscript');
    noScript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${this.gtmId}"
            height="0" width="0"
            style="display:none;visibility:hidden"></iframe>`;
    document.body.appendChild(noScript);
    initGoogleAnalytics(this.gtmId);
  }

  includeScript(content: string, documentPart: string = 'body'): void {
    var script = document.createElement('script');
    script.innerHTML = content;
    script.setAttribute('nonce', 'test_gtm_dubai7s');
    if (documentPart === 'body') {
      document.body.appendChild(script);
    }
    if (documentPart === 'head') {
      document.head.appendChild(script);
    }
  }

  /**
   * Called on all routes.
   */
  pageViewEvent(pagePath: string): void {
    if (this.allowed) {
      gtag('event', 'page_view', {
        page_path: pagePath,
        ...this.utmParams,
      });
    }
  }
}
